/* eslint-disable jsx-a11y/no-autofocus */
import InputModal from '@pcln/input-modal';
import { themeGet } from '@styled-system/theme-get';
import { Box, Button, Flex, Grid, ShadowOverlay, Text, Tooltip, getPaletteColor } from 'pcln-design-system';
import { ArrowLeft as LeftIcon, User as UserIcon } from 'pcln-icons';
import Popover from 'pcln-popover';
import React, { memo, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { DEFAULT_MAX_TRAVELERS } from '../constants';
import TravelerSelectionContent from '../TravelerSelectionContent';
import useTravelerSelection from '../useTravelerSelection';
const Container = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "TravelerSelection__Container",
  componentId: "sc-ntt12-0"
})(["position:relative;& > :last-child{width:100%;", ";}"], props => props.isOpen ? 'z-index: 1502' : '');
const StyledPopover = /*#__PURE__*/styled(Popover).withConfig({
  displayName: "TravelerSelection__StyledPopover",
  componentId: "sc-ntt12-1"
})(["position:relative;max-width:388px;"]);
const TravelerSelectionButton = /*#__PURE__*/styled(Button).withConfig({
  displayName: "TravelerSelection__TravelerSelectionButton",
  componentId: "sc-ntt12-2"
})(["&&&{display:flex;align-items:center;height:100%;font-size:", "px;font-weight:normal;border-width:1px;border-style:solid;border-color:", ";outline:none;&:focus{border:1px solid ", ";box-shadow:0 0 0 2px ", ";}& > *{width:100%;}", "{font-size:", "px;}}"], themeGet('fontSizes.2'), props => props.hasError ? getPaletteColor('error.base')(props) : getPaletteColor('border.base')(props), getPaletteColor('primary.base'), getPaletteColor('primary.base'), themeGet('mediaQueries.lg'), themeGet('fontSizes.1'));
const StyledTooltip = /*#__PURE__*/styled(Tooltip).withConfig({
  displayName: "TravelerSelection__StyledTooltip",
  componentId: "sc-ntt12-3"
})(["top:", "px;&::after{&:focus{outline:none;}}"], props => props.errorHeight);
const StyledBackButton = /*#__PURE__*/styled(Button).withConfig({
  displayName: "TravelerSelection__StyledBackButton",
  componentId: "sc-ntt12-4"
})(["z-index:1502;"]);
/** @public */
const TravelerSelection = ({
  ageSelectEnabled,
  borderRadius = 'lg',
  className,
  disclaimer,
  errorHeight = 20,
  errorMessage,
  fireChildCountImpression = () => {},
  hasShadowEffect,
  hideTooltip,
  initialValues,
  isInternationalFlight,
  isHotel = false,
  isMobile = false,
  isTravelerSelectionDisabled,
  isWhiteLabel,
  lapInfantLabel,
  maxAdults,
  maxChildren,
  maxRooms,
  maxRoomsMessage,
  maxRoomsUrl,
  fireMaxRoomsOnClick,
  maxTravelers = DEFAULT_MAX_TRAVELERS,
  roomsEnabled,
  shouldCountLapInfant,
  showBackButton = false,
  subLabelMap,
  tooltipErrorMessageId = 'traveler-selection-tooltip-error',
  trapFocus = true,
  usePopoverPortal = false,
  onChange,
  onClose,
  onOpen,
  shouldAllowInternationalLapInfants,
  ...props
}) => {
  const useTravelerSelectionProps = useMemo(() => ({
    initialValues: {
      rooms: roomsEnabled ? 1 : 0,
      ...initialValues
    },
    ageSelectEnabled: !!ageSelectEnabled,
    errorMessage,
    isInternationalFlight: !!isInternationalFlight,
    isWhiteLabel,
    maxTravelers,
    shouldCountLapInfant,
    shouldAllowInternationalLapInfants
  }), [errorMessage, initialValues, roomsEnabled]);
  const [isOpen, setOpen] = useState(false);
  const state = useTravelerSelection(useTravelerSelectionProps);
  const modalIdx = 'traveler-selection-modal';
  const popoverIdx = 'traveler-selection-popover';
  const selectorAriaLabel = 'Select traveler information';
  const showTooltip = !isOpen && !hideTooltip && state.errorMessage;
  useEffect(() => {
    if (!isOpen) return;
    if (!isMobile) {
      const selectTravelerBtn = document.querySelector(`[aria-label="${selectorAriaLabel}"]`);
      const popoverMenuBtn = document.querySelector(`[aria-describedby="dialog-description-${popoverIdx}"]`);
      const popoverMenu = document.querySelector(`[aria-describedby="dialog-description-${popoverIdx}"] > div`);
      popoverMenu.style.opacity = '0';
      setTimeout(() => {
        const {
          top: btnTop
        } = selectTravelerBtn.getBoundingClientRect();
        const {
          bottom: menuBottom,
          height: menuHeight
        } = popoverMenu.getBoundingClientRect();
        const isTopPlacement = popoverMenuBtn.attributes['data-placement'] === 'top';
        let currentTopValue;
        let newTopValue;
        if (menuBottom > window.innerHeight) {
          currentTopValue = parseInt(popoverMenu.style.top.replace(/^\D+/g, '')) || 0;
          /* c8 ignore next */
          newTopValue = isTopPlacement ? currentTopValue - menuHeight : Math.max(btnTop - menuHeight - 30, 0);
        } else {
          currentTopValue = parseInt(popoverMenuBtn.style.top.replace(/^\D+/g, '')) || 0;
          /* c8 ignore next */
          newTopValue = isTopPlacement ? currentTopValue - menuHeight : currentTopValue - 8;
        }
        popoverMenuBtn.style.top = newTopValue + 'px';
        popoverMenu.style.opacity = '1';
      }, 10);
    }
  }, [isOpen]);
  const popoverPortal = 'traveler-selection-popover-place';
  useEffect(() => {
    const rooms = roomsEnabled ? useTravelerSelectionProps.initialValues.rooms || 1 : 0;
    state.setRooms(rooms);
    if (onChange) {
      onChange({
        ...state,
        rooms
      });
    }
  }, [roomsEnabled, useTravelerSelectionProps.initialValues.rooms]);
  if (state.children && isOpen && fireChildCountImpression) {
    fireChildCountImpression();
  }
  function handleOpen() {
    setOpen(true);
    onOpen?.();
    if (onChange) {
      onChange(state);
    }
  }
  function handleClose() {
    setOpen(false);
    onClose?.();
    /* istanbul ignore else */
    if (onChange) {
      onChange(state);
    }
  }
  function handleContentClose() {
    onClose?.();
  }
  function PopoverContent({
    handleClose
  }) {
    return React.createElement(TravelerSelectionContent, {
      ageSelectEnabled: ageSelectEnabled,
      autoFocus: trapFocus,
      disclaimer: disclaimer,
      handleClose: handleClose,
      isHotel: isHotel,
      isPopover: !isMobile,
      lapInfantLabel: lapInfantLabel,
      maxAdults: maxAdults,
      maxChildren: maxChildren,
      maxRooms: maxRooms,
      fireMaxRoomsOnClick: fireMaxRoomsOnClick,
      maxRoomsMessage: maxRoomsMessage,
      maxRoomsUrl: maxRoomsUrl,
      roomsEnabled: roomsEnabled,
      shouldCountLapInfant: shouldCountLapInfant,
      state: state,
      subLabelMap: subLabelMap
    });
  }
  return React.createElement(React.Fragment, null, React.createElement(Container, {
    className: className,
    isOpen: isOpen,
    width: 1,
    ...props
  }, !isMobile && React.createElement(React.Fragment, null, hasShadowEffect && isOpen && React.createElement(ShadowOverlay, {
    zIndex: 2,
    onClick: handleClose,
    "data-testid": 'traveler-selection-overlay'
  }), React.createElement(StyledPopover, {
    idx: popoverIdx,
    hideArrow: true,
    trapFocus: trapFocus,
    ariaLabel: selectorAriaLabel,
    width: '388px',
    zIndex: 1502,
    placement: 'bottom',
    renderContent: PopoverContent,
    onOpen: handleOpen,
    onClose: handleClose,
    querySelectorPortal: `.${popoverPortal}`
  }, React.createElement(TravelerSelectionButton, {
    "aria-errormessage": state.errorMessage ? tooltipErrorMessageId : undefined,
    "aria-invalid": state.errorMessage ? 'true' : 'false',
    borderRadius: borderRadius,
    color: 'background.lightest',
    disabled: isTravelerSelectionDisabled,
    hasError: state.errorMessage && !isOpen,
    isOpen: isOpen,
    width: 1,
    onClick: handleOpen,
    p: 2,
    pl: isOpen && showBackButton && 0
  }, isOpen && showBackButton && React.createElement(StyledBackButton, {
    color: 'primary',
    onClick: handleClose,
    size: 'small',
    "data-testid": 'traveler-back-button',
    mr: 2
  }, React.createElement(LeftIcon, {
    color: 'white'
  })), React.createElement(Flex, {
    alignItems: 'center',
    width: 1
  }, React.createElement(UserIcon, {
    color: 'primary',
    mr: 2
  }), state.label), showTooltip && React.createElement(StyledTooltip, {
    errorHeight: errorHeight,
    id: tooltipErrorMessageId,
    width: 250,
    color: 'error',
    role: 'alert',
    right: true,
    bottom: true,
    zIndex: 2
  }, state.errorMessage)))), isMobile && React.createElement(React.Fragment, null, React.createElement(TravelerSelectionButton, {
    "aria-errormessage": state.errorMessage ? tooltipErrorMessageId : undefined,
    "aria-invalid": state.errorMessage ? 'true' : 'false',
    borderRadius: borderRadius,
    color: 'background.lightest',
    disabled: isTravelerSelectionDisabled,
    hasError: state.errorMessage && !isOpen,
    type: 'button',
    width: 1,
    onClick: handleOpen,
    p: 2
  }, React.createElement(Flex, {
    alignItems: 'center',
    width: 1
  }, React.createElement(UserIcon, {
    color: 'primary',
    mr: 2
  }), state.label), showTooltip && React.createElement(StyledTooltip, {
    id: tooltipErrorMessageId,
    width: 250,
    color: 'error',
    role: 'alert',
    right: true,
    bottom: true
  }, state.errorMessage)), React.createElement(InputModal, {
    id: modalIdx,
    footer: React.createElement(Button, {
      type: 'button',
      borderRadius: 'lg',
      onClick: handleClose
    }, "Done"),
    input: React.createElement(Grid, {
      alignItems: 'center',
      templateColumns: '1fr auto',
      pb: 2
    }, React.createElement(Flex, {
      justifyContent: 'center'
    }, React.createElement(Text, {
      fontSize: '3',
      fontWeight: 'bold',
      color: 'primary.dark'
    }, "Travelers")), React.createElement(Box, {
      width: '40px',
      height: '40px',
      ml: '12px'
    })),
    inputProps: {
      px: 2,
      py: 2
    },
    isOpen: isOpen,
    subheading: React.createElement(Flex, {
      justifyContent: 'center',
      width: 1
    }, React.createElement(Text, {
      fontSize: '2',
      fontWeight: 'medium',
      color: 'text',
      mb: 2
    }, state.label)),
    zIndex: 1502,
    onClose: handleClose
  }, React.createElement(TravelerSelectionContent, {
    ageSelectEnabled: ageSelectEnabled,
    autoFocus: trapFocus,
    disclaimer: disclaimer,
    isHotel: isHotel,
    handleClose: handleContentClose,
    lapInfantLabel: lapInfantLabel,
    maxAdults: maxAdults,
    maxChildren: maxChildren,
    maxRooms: maxRooms,
    maxRoomsMessage: maxRoomsMessage,
    maxRoomsUrl: maxRoomsUrl,
    roomsEnabled: roomsEnabled,
    shouldCountLapInfant: shouldCountLapInfant,
    state: state,
    subLabelMap: subLabelMap,
    fireMaxRoomsOnClick: fireMaxRoomsOnClick
  })))), usePopoverPortal ? React.createElement(Box, {
    className: popoverPortal,
    width: 0,
    "data-testid": 'popover-portal'
  }) : null);
};
export default memo(TravelerSelection);